import { Controller } from '@hotwired/stimulus';
import { csrfHeaders } from '../services/form.services';
import { manualAnalysisStep, updateManualAnalysisProgress } from '../services/manual-analysis-progress';

type AssetState = {
    id: number;
    role: 'food' | 'reference' | 'manual';
    position: number;
    status: string;
    analysisStatus?: string | null;
    error?: string | null;
    foodPosition?: number | null;
    review?: {
        name: string;
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        additionalInfo: string;
        items: Array<{ name: string; quantity?: number | null; size: string }>;
    } | null;
};

type AnalysisQueueDetail = {
    primaryFile: File;
    secondaryFile?: File | null;
    additionalInfo?: string;
    references?: Array<{ file: File; foodPosition: number }>;
};

type ManualQueueDetail = {
    description: string;
};

type SessionState = {
    id: number;
    status: string;
    error?: string | null;
    resultUrl?: string;
    assets: AssetState[];
};

export class QueuedMealController extends Controller {
    declare readonly sessionIdValue: number;
    declare readonly hasSessionIdValue: boolean;
    declare readonly showCaloriesValue: boolean;
    declare readonly assetsTarget: HTMLOListElement;
    declare readonly statusTarget: HTMLElement;
    declare readonly finishTarget: HTMLButtonElement;
    declare readonly hasFinishTarget: boolean;
    declare readonly compactFinishTarget: HTMLButtonElement;
    declare readonly hasCompactFinishTarget: boolean;
    declare readonly panelTarget: HTMLElement;
    declare readonly drawerTarget: HTMLDetailsElement;
    declare readonly countTarget: HTMLElement;
    declare readonly summaryStatusTarget: HTMLElement;
    declare readonly spinnerIconTarget: HTMLElement;
    declare readonly notificationReminderTarget: HTMLElement;
    declare readonly hasNotificationReminderTarget: boolean;

    static targets = [
        'assets',
        'status',
        'finish',
        'compactFinish',
        'panel',
        'drawer',
        'count',
        'summaryStatus',
        'spinnerIcon',
        'notificationReminder',
    ];
    static values = { sessionId: Number, showCalories: { type: Boolean, default: true } };

    private sessionId: number | null = null;
    private assets: AssetState[] = [];
    private pollTimer: number | null = null;
    private uploading = false;
    private finalising = false;
    private additionalInfo = '';
    private collectingMoreImages = false;
    private completionPromptShown = false;
    private assetsRenderKey = '';

    connect(): void {
        this.drawerTarget.open = false;
        void this.refreshNotificationReminder();
        if (this.hasSessionIdValue && this.sessionIdValue > 0) {
            this.sessionId = this.sessionIdValue;
            this.element.setAttribute('data-meal-queue-session-id', String(this.sessionId));
            this.collectingMoreImages = true;
            this.moveProgressToQueue();
            this.showStatus('Refreshing your combined meal analysis…');
            this.startPolling();
        }
    }

    disconnect(): void {
        this.stopPolling();
    }

    startFromAnalysis = async (event: CustomEvent<AnalysisQueueDetail>): Promise<void> => {
        if (this.sessionId || this.uploading) return;
        const primaryFile = event.detail?.primaryFile;
        if (!primaryFile) return;

        this.additionalInfo = event.detail?.additionalInfo || '';
        this.uploading = true;
        this.showStatus('Adding your photo to the analysis queue…');
        try {
            await this.ensureSession();
            await this.uploadAsset(
                primaryFile,
                'food',
                this.additionalInfo,
                undefined,
                event.detail?.secondaryFile,
            );
            for (const reference of event.detail?.references || []) {
                await this.uploadAsset(reference.file, 'reference', '', reference.foodPosition);
            }
            this.dispatch('ready');
            this.showStatus(
                'Photo queued. Upload another image to add it to this meal, or finish the analysis.',
            );
            this.startPolling();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'The photo could not be queued.';
            if (!this.assets.length) {
                this.panelTarget.classList.add('hidden');
                this.dispatch('error', { detail: { message } });
            }
            this.showStatus(message, true);
        } finally {
            this.uploading = false;
            this.updateFinishState();
            this.continueOrFinalize();
        }
    };

    addImageFromAnalysis = async (event: CustomEvent<AnalysisQueueDetail>): Promise<void> => {
        const primaryFile = event.detail?.primaryFile;
        if (!primaryFile || this.uploading || !this.sessionId) return;
        this.uploading = true;
        this.showStatus('Adding the new image to your meal…');
        try {
            const baseFoodPosition = this.assets.filter((asset) => asset.role === 'food').length;
            await this.uploadAsset(
                primaryFile,
                'food',
                event.detail?.additionalInfo || '',
                undefined,
                event.detail?.secondaryFile,
            );
            for (const reference of event.detail?.references || []) {
                await this.uploadAsset(
                    reference.file,
                    'reference',
                    '',
                    baseFoodPosition + reference.foodPosition,
                );
            }
            this.showStatus(
                'Image added. You can upload another image or finish the meal analysis.',
            );
            this.drawerTarget.open = false;
            this.dispatch('image-added');
            this.startPolling();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'The photo could not be queued.';
            this.showStatus(message, true);
            this.dispatch('image-error', { detail: { message } });
        } finally {
            this.uploading = false;
            this.updateFinishState();
            this.continueOrFinalize();
        }
    };

    queueManualEntry = async (event: CustomEvent<ManualQueueDetail>): Promise<void> => {
        const description = event.detail?.description?.trim();
        if (!description || this.uploading || this.finalising) return;
        const existingSession = Boolean(this.sessionId);
        this.uploading = true;
        this.showStatus(
            existingSession
                ? 'Adding the manual item to your meal…'
                : 'Starting your manual analysis…',
        );
        try {
            await this.ensureSession();
            await this.uploadManualAsset(description);
            if (!existingSession) this.dispatch('ready');
            this.showStatus(existingSession
                ? 'Manual item added. Add another item or complete this meal when it is ready.'
                : 'Analysing your meal…');
            this.drawerTarget.open = false;
            this.dispatch('manual-added', { detail: { initial: !existingSession } });
            this.startPolling();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'The manual item could not be queued.';
            if (!this.assets.length) this.panelTarget.classList.add('hidden');
            this.showStatus(message, true);
            this.dispatch('manual-error', { detail: { message } });
        } finally {
            this.uploading = false;
            this.updateFinishState();
            this.continueOrFinalize();
        }
    };

    private moveProgressToQueue(): void {
        this.element.setAttribute('data-analysis-progress-location', 'queue');
        const progress = this.element.querySelector<HTMLElement>('[data-manual-analysis-progress]');
        if (progress) progress.hidden = true;
    }

    addMore = (): void => {
        if (!this.sessionId || this.finalising) return;
        this.collectingMoreImages = true;
        this.completionPromptShown = false;
        this.moveProgressToQueue();
        this.syncPanelVisibility();
        this.drawerTarget.open = false;
        this.dispatch('workspace');
    };

    finish = async (event?: Event): Promise<void> => {
        event?.preventDefault();
        event?.stopPropagation();
        if (!this.sessionId || this.finalising || !this.allUsableAssetsReady()) return;
        this.finalising = true;
        this.setFinishLoading(true);
        if (this.hasFinishTarget) this.finishTarget.disabled = true;
        if (this.hasCompactFinishTarget) this.compactFinishTarget.disabled = true;
        this.showStatus('All images are ready. Preparing your combined results…');
        try {
            const response = await fetch(`/analyse/sessions/${this.sessionId}/finish`, {
                method: 'POST',
                headers: csrfHeaders(),
                credentials: 'same-origin',
            });
            const payload = (await response.json()) as SessionState & { message?: string };
            if (!response.ok)
                throw new Error(payload.message || 'The meal could not be finalised.');
            this.applyState(payload);
            this.startPolling(true);
        } catch (error) {
            this.finalising = false;
            this.setFinishLoading(false);
            this.showStatus(
                error instanceof Error ? error.message : 'The meal could not be finalised.',
                true,
            );
            this.updateFinishState();
        }
    };

    cancel = async (): Promise<void> => {
        if (!this.sessionId) {
            window.location.assign('/analyse');
            return;
        }
        if (!window.confirm('Cancel this meal and delete its queued photos?')) return;
        try {
            await fetch(`/analyse/sessions/${this.sessionId}/cancel`, {
                method: 'POST',
                headers: csrfHeaders(),
                credentials: 'same-origin',
            });
        } finally {
            window.location.assign('/analyse');
        }
    };

    private async ensureSession(): Promise<void> {
        if (this.sessionId) return;
        const body = new FormData();
        if (this.additionalInfo) body.append('additional_info', this.additionalInfo);
        const response = await fetch('/analyse/sessions', {
            method: 'POST',
            headers: csrfHeaders(),
            body,
            credentials: 'same-origin',
        });
        const payload = (await response.json()) as SessionState & { message?: string };
        if (!response.ok || !payload.id)
            throw new Error(payload.message || 'A meal session could not be started.');
        this.sessionId = payload.id;
        this.element.setAttribute('data-meal-queue-session-id', String(this.sessionId));
    }

    private async uploadAsset(
        file: File,
        role: 'food' | 'reference',
        additionalInfo = '',
        foodPosition?: number,
        secondaryFile?: File | null,
    ): Promise<void> {
        if (!this.sessionId) throw new Error('A meal session could not be started.');
        const body = new FormData();
        body.append('image', file, file.name);
        if (role === 'food' && secondaryFile) {
            body.append('image_secondary', secondaryFile, secondaryFile.name);
        }
        body.append('role', role);
        if (additionalInfo) body.append('additional_info', additionalInfo);
        if (role === 'reference' && foodPosition !== undefined) {
            body.append('food_position', String(foodPosition));
        }
        const response = await fetch(`/analyse/sessions/${this.sessionId}/assets`, {
            method: 'POST',
            headers: csrfHeaders(),
            body,
            credentials: 'same-origin',
        });
        const payload = (await response.json()) as { asset?: AssetState; message?: string };
        if (!response.ok || !payload.asset)
            throw new Error(payload.message || 'The photo could not be queued.');
        this.assets.push(payload.asset);
        this.renderAssets();
    }

    private async uploadManualAsset(description: string): Promise<void> {
        if (!this.sessionId) throw new Error('A meal session could not be started.');
        const body = new FormData();
        body.append('description', description);
        const response = await fetch(`/analyse/sessions/${this.sessionId}/manual`, {
            method: 'POST',
            headers: csrfHeaders(),
            body,
            credentials: 'same-origin',
        });
        const payload = (await response.json()) as { asset?: AssetState; message?: string };
        if (!response.ok || !payload.asset)
            throw new Error(payload.message || 'The manual item could not be queued.');
        this.assets.push(payload.asset);
        this.renderAssets();
    }

    private startPolling(finalising = false): void {
        this.stopPolling();
        void this.poll(finalising);
    }

    private async poll(finalising: boolean): Promise<void> {
        if (!this.sessionId) return;
        try {
            const response = await fetch(`/analyse/sessions/${this.sessionId}/status`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                cache: 'no-store',
            });
            if (!response.ok) throw new Error('Unable to refresh the meal queue.');
            const state = (await response.json()) as SessionState;
            this.applyState(state);
            if (state.status === 'completed' && state.resultUrl) {
                this.showStatus('Your combined meal is ready. Opening the result…');
                window.location.assign(state.resultUrl);
                return;
            }
            if (state.status === 'failed') {
                this.showStatus(state.error || 'The combined meal could not be completed.', true);
                return;
            }
            const hasActiveAssets = state.assets.some((asset) =>
                ['queued', 'processing'].includes(asset.status),
            );
            if (hasActiveAssets || finalising || state.status === 'finalising') {
                this.pollTimer = window.setTimeout(() => void this.poll(finalising), 1500);
            }
        } catch (error) {
            this.showStatus(
                error instanceof Error ? error.message : 'Unable to refresh the meal queue.',
                true,
            );
            this.pollTimer = window.setTimeout(() => void this.poll(finalising), 3000);
        }
    }

    private applyState(state: SessionState): void {
        this.finalising = state.status === 'finalising';
        this.setFinishLoading(this.finalising);
        this.assets = state.assets || [];
        this.updateMealProgress(state);
        this.renderAssets();
        if (state.status === 'finalising') {
            this.showStatus('Combining and preparing your meal, hold on a sec!');
        } else if (state.status === 'building' && this.allUsableAssetsReady() && !this.uploading) {
            if (this.collectingMoreImages) {
                const queuedMealItems = this.assets.filter((asset) =>
                    ['food', 'manual'].includes(asset.role),
                );
                this.showStatus('Analysis complete. Complete this meal or add another item.');
                if (queuedMealItems.length > 1 && !this.completionPromptShown) {
                    this.completionPromptShown = true;
                    this.drawerTarget.open = true;
                }
            } else {
                void this.finish();
            }
        }
    }

    private updateMealProgress(state: SessionState): void {
        const mealAssets = this.assets.filter((asset) => ['food', 'manual'].includes(asset.role));
        if (!mealAssets.length) return;
        if (state.status === 'finalising' || state.status === 'completed' || state.status === 'failed') {
            updateManualAnalysisProgress(this.element, state.status, state.error || undefined);
            return;
        }
        const failed = mealAssets.find((asset) => asset.status === 'failed');
        if (failed) {
            updateManualAnalysisProgress(this.element, 'failed', failed.error || 'A meal item could not be analysed.');
            return;
        }
        // Show the least advanced meal item, rather than suggesting all are done.
        const current = mealAssets.reduce((earliest, asset) =>
            manualAnalysisStep(asset.analysisStatus || asset.status) < manualAnalysisStep(earliest.analysisStatus || earliest.status)
                ? asset : earliest,
        );
        updateManualAnalysisProgress(this.element, current.analysisStatus || current.status);
    }

    private renderAssets(): void {
        const foodImageCount = this.assets.filter((asset) =>
            ['food', 'manual'].includes(asset.role),
        ).length;
        this.countTarget.textContent = String(foodImageCount);
        this.countTarget.setAttribute(
            'aria-label',
            `${foodImageCount} queued meal item${foodImageCount === 1 ? '' : 's'}`,
        );
        this.assetsTarget.classList.toggle('hidden', this.assets.length === 0);
        const renderKey = JSON.stringify(this.assets);
        if (renderKey === this.assetsRenderKey) {
            this.syncPanelVisibility();
            this.updateQueueActivityIcon();
            this.updateFinishState();
            return;
        }
        const openReviewAssetIds = new Set(
            Array.from(
                this.assetsTarget.querySelectorAll<HTMLDetailsElement>(
                    '[data-queue-review-card][open]',
                ),
            )
                .map((card) => card.dataset.queueReviewAssetId)
                .filter((assetId): assetId is string => Boolean(assetId)),
        );
        this.assetsTarget.innerHTML = this.assets
            .map((asset) => {
                const complete = asset.status === 'completed';
                const failed = asset.status === 'failed';
                const icon = complete
                    ? 'fa-check'
                    : failed
                      ? 'fa-exclamation'
                      : 'fa-spinner fa-spin';
                const tone = complete
                    ? 'bg-green-100 text-green-700'
                    : failed
                      ? 'bg-red-100 text-red-700'
                      : 'bg-zinc-100 text-zinc-600';
                const label =
                    asset.role === 'food'
                        ? 'Meal image'
                        : asset.role === 'manual'
                          ? 'Manual item'
                          : 'Packaging reference';
                const analysisStatus = complete ? 'completed' : asset.analysisStatus || asset.status;
                const stage = manualAnalysisStep(analysisStatus);
                const status = complete ? 'Ready' : failed ? 'Needs attention' : ({
                    queued: 'Queued', requesting: 'Preparing', recovering: 'Preparing',
                    processing: 'Preparing', identifying_foods: 'Identifying ingredients',
                    calculating_nutrition: 'Calculating nutrition', finalising: 'Finalising',
                } as Record<string, string>)[analysisStatus] || 'Analysing';
                const progress = `<span class="queue-item-progress" data-queue-item-progress="${asset.id}" data-failed="${failed}" role="progressbar" aria-label="${label} ${asset.position + 1} analysis" aria-valuemin="0" aria-valuemax="4" aria-valuenow="${failed ? 0 : stage}" aria-valuetext="${status}"><span style="width: ${failed ? 0 : stage * 25}%"></span></span>`;
                if (asset.role !== 'reference' && complete && asset.review) {
                    const review = asset.review;
                    const safeName = this.escapeHtml(review.name);
                    const foodItems = review.items
                        .map((item) => {
                            const quantity = item.quantity
                                ? `${this.formatMetric(item.quantity)} × `
                                : '';
                            return `<span class="inline-flex max-w-full flex-wrap items-center gap-x-1 rounded-xl border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs leading-5 text-zinc-700"><span class="break-words font-medium text-zinc-900">${this.escapeHtml(item.name)}</span><span class="text-zinc-400">${quantity}${this.escapeHtml(item.size)}</span></span>`;
                        })
                        .join('');
                    const reviewBase = `/analyse/sessions/${this.sessionId}/assets/${asset.id}/review`;
                    const calorieMetric = this.showCaloriesValue
                        ? `<span><strong class="block text-sm text-zinc-900">${this.formatMetric(review.calories)}</strong><small class="text-[10px] text-zinc-500">calories</small></span>`
                        : '';
                    const metricColumns = this.showCaloriesValue ? 'grid-cols-4' : 'grid-cols-3';
                    return `<li>
                    <details data-queue-review-card data-queue-review-asset-id="${asset.id}" class="group overflow-hidden rounded-xl border border-green-200 bg-white shadow-sm">
                        <summary class="flex min-h-14 cursor-pointer list-none items-center gap-3 px-3 py-2.5 marker:hidden [&::-webkit-details-marker]:hidden">
                            <span class="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700"><i class="fas fa-check" aria-hidden="true"></i></span>
                            <span class="min-w-0 flex-1"><span class="block truncate text-sm font-semibold text-zinc-900">${safeName}</span><span class="block text-xs text-green-700">Ready to review</span>${progress}</span>
                            <i class="fas fa-chevron-down text-xs text-zinc-400 transition group-open:rotate-180" aria-hidden="true"></i>
                        </summary>
                        <div class="border-t border-green-100 px-3 pb-3 pt-3">
                            <div class="grid ${metricColumns} divide-x divide-zinc-200 rounded-xl bg-green-50/70 px-1 py-2 text-center">
                                ${calorieMetric}
                                <span><strong class="block text-sm text-zinc-900">${this.formatMetric(review.protein)}g</strong><small class="text-[10px] text-zinc-500">protein</small></span>
                                <span><strong class="block text-sm text-zinc-900">${this.formatMetric(review.carbs)}g</strong><small class="text-[10px] text-zinc-500">carbs</small></span>
                                <span><strong class="block text-sm text-zinc-900">${this.formatMetric(review.fat)}g</strong><small class="text-[10px] text-zinc-500">fat</small></span>
                            </div>
                            <div class="mt-3 flex flex-wrap items-start gap-2">${foodItems}</div>
                            <div class="mt-3 grid grid-cols-2 gap-2">
                                <a href="${reviewBase}?action=edit" data-turbo-frame="_top" class="rounded-xl border border-green-200 bg-white px-3 py-2 text-center text-xs font-semibold text-green-700"><i class="fas fa-pen mr-1" aria-hidden="true"></i>Edit values</a>
                                <a href="${reviewBase}?action=reanalyse" data-turbo-frame="_top" class="rounded-xl border border-green-200 bg-white px-3 py-2 text-center text-xs font-semibold text-green-700"><i class="fas fa-rotate mr-1" aria-hidden="true"></i>Re-analyse</a>
                            </div>
                        </div>
                    </details>
                </li>`;
                }
                return `<li class="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-3 shadow-sm">
                <span class="inline-flex size-9 shrink-0 items-center justify-center rounded-full ${tone}"><i class="fas ${icon}" aria-hidden="true"></i></span>
                <span class="min-w-0 flex-1"><span class="block text-sm font-semibold text-zinc-900">${label} ${asset.position + 1}</span><span class="block text-xs text-zinc-500">${status}</span>${progress}</span>
            </li>`;
            })
            .join('');
        this.assetsRenderKey = renderKey;
        this.assetsTarget
            .querySelectorAll<HTMLDetailsElement>('[data-queue-review-card]')
            .forEach((card) => {
                card.open = openReviewAssetIds.has(card.dataset.queueReviewAssetId || '');
            });
        this.syncPanelVisibility();
        this.updateQueueActivityIcon();
        this.updateFinishState();
    }

    private formatMetric(value: number): string {
        return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
    }

    private escapeHtml(value: string): string {
        return value.replace(
            /[&<>'"]/g,
            (character) =>
                ({
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    "'": '&#39;',
                    '"': '&quot;',
                })[character] || character,
        );
    }

    private updateQueueActivityIcon(): void {
        const analysing =
            this.uploading ||
            this.assets.some((asset) => ['queued', 'processing'].includes(asset.status));
        this.spinnerIconTarget.classList.toggle('hidden', !analysing);
        this.spinnerIconTarget.classList.toggle('inline-flex', analysing);
    }

    private updateFinishState(): void {
        const usableAssets = this.assets.filter((asset) => asset.status !== 'failed');
        const hasFood = usableAssets.some((asset) => ['food', 'manual'].includes(asset.role));
        if (this.hasFinishTarget) {
            this.finishTarget.disabled =
                !hasFood || !this.allUsableAssetsReady() || this.uploading || this.finalising;
        }
        if (this.hasCompactFinishTarget) {
            const canComplete =
                hasFood && this.allUsableAssetsReady() && !this.uploading && !this.finalising;
            this.compactFinishTarget.disabled = !canComplete;
            this.compactFinishTarget.classList.toggle('hidden', !canComplete && !this.finalising);
            this.compactFinishTarget.classList.toggle(
                'inline-flex',
                canComplete || this.finalising,
            );
        }
    }

    private allUsableAssetsReady(): boolean {
        const usableAssets = this.assets.filter((asset) => asset.status !== 'failed');
        return (
            usableAssets.some((asset) => ['food', 'manual'].includes(asset.role)) &&
            usableAssets.every((asset) => asset.status === 'completed')
        );
    }

    private continueOrFinalize(): void {
        if (!this.sessionId || this.finalising) return;
        if (this.allUsableAssetsReady() && !this.collectingMoreImages) {
            void this.finish();
            return;
        }
        this.startPolling();
    }

    private showStatus(message: string, error = false): void {
        this.syncPanelVisibility();
        this.summaryStatusTarget.textContent = message;
        this.statusTarget.classList.toggle('hidden', !error);
        if (error) this.statusTarget.textContent = message;
    }

    private syncPanelVisibility(): void {
        const mealItemCount = this.assets.filter((asset) =>
            ['food', 'manual'].includes(asset.role),
        ).length;
        const shouldShowQueue =
            mealItemCount >= 2 || (mealItemCount >= 1 && this.collectingMoreImages);
        this.panelTarget.classList.toggle('hidden', !shouldShowQueue);
    }

    private async refreshNotificationReminder(): Promise<void> {
        if (!this.hasNotificationReminderTarget) return;
        if (
            !('Notification' in window) ||
            !('serviceWorker' in navigator) ||
            !('PushManager' in window) ||
            !window.isSecureContext
        ) {
            return;
        }

        this.notificationReminderTarget.classList.remove('hidden');
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            const subscription = registration
                ? await registration.pushManager.getSubscription()
                : null;
            const notificationsEnabled =
                Notification.permission === 'granted' && subscription !== null;
            this.notificationReminderTarget.classList.toggle('hidden', notificationsEnabled);
        } catch (error) {
            console.warn('meal queue notification status could not be checked', error);
        }
    }

    private setFinishLoading(loading: boolean): void {
        this.element
            .querySelectorAll<HTMLElement>('[data-queued-meal-button-label]')
            .forEach((label) => {
                label.textContent = loading ? 'Preparing' : 'Complete meal';
            });
        this.element
            .querySelectorAll<HTMLElement>('[data-queued-meal-loading-icon]')
            .forEach((icon) => {
                icon.hidden = !loading;
                icon.classList.toggle('hidden', !loading);
            });
        this.element
            .querySelectorAll<HTMLElement>('[data-queued-meal-idle-icon]')
            .forEach((icon) => {
                icon.classList.toggle('hidden', loading);
            });
    }

    private stopPolling(): void {
        if (this.pollTimer !== null) window.clearTimeout(this.pollTimer);
        this.pollTimer = null;
    }
}
